<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ProfilePictureInput from "@/components/ui/ProfilePictureInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import TextInput from "@/components/Inputs/TextInput.vue";

type Props = {
  groupName: string;
  memberName: string;
  pictureUrl?: string;
  updating?: boolean;
  nameValid?: boolean;
  profileValid?: boolean;
};

type Emits = {
  (e: "update:memberName", value: string): void;
  (e: "picture-change", file: File): void;
  (e: "save"): void;
};

const props = withDefaults(defineProps<Props>(), {
  pictureUrl: "",
  updating: false,
  nameValid: true,
  profileValid: true,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const localMemberName = computed({
  get: () => props.memberName,
  set: (value: string) => emit("update:memberName", value),
});

function handlePictureChange(file: File): void {
  emit("picture-change", file);
}

function handleSave(): void {
  emit("save");
}
</script>

<template>
  <div
    class="rounded-lg border-2 border-black-dark bg-white p-4 shadow-yellow-hard-sm dark:bg-kekkle-dark"
  >
    <!-- Group Header -->
    <div class="mb-4 flex items-center gap-2">
      <span class="text-sm font-medium text-gray-600 dark:text-slate-400">
        {{ t("settings.group_name") }}:
      </span>
      <h3 class="text-base font-bold text-gray-900 dark:text-slate-100">
        {{ groupName }}
      </h3>
    </div>

    <!-- Profile Section - Side by side layout -->
    <div class="mb-4 flex gap-4">
      <profile-picture-input
        :current-image-url="pictureUrl"
        :label="t('settings.profile_picture')"
        @change="handlePictureChange"
      />

      <!-- Name Input - Takes remaining space -->
      <div class="flex-1">
        <text-input
          v-model="localMemberName"
          :placeholder="t('global.inputs.placeholders.your_name')"
          :label="t('global.inputs.labels.name')"
        />
        <p v-show="!nameValid" class="mt-1 text-sm text-red-600">
          {{ t("global.inputs.errors.name") }}
        </p>
      </div>
    </div>

    <!-- Save Button -->
    <default-button
      color="yellow"
      :loading="updating"
      :disabled="!profileValid"
      full-width
      @click="handleSave"
    >
      <span v-if="updating">
        {{ t("global.inputs.labels.saving") }}
      </span>
      <span v-else>
        {{ t("global.inputs.labels.save") }}
      </span>
    </default-button>
  </div>
</template>
